/**
 * Captura beforeinstallprompt o mais cedo possível (antes do React montar).
 * O Chrome dispara o evento uma vez; se perdermos, some o botão Instalar.
 */
const DISMISS_KEY = 'sa_pwa_install_dismissed_v1';
const DISMISS_DAYS = 21;
const MODAL_SHOWN_KEY = 'sa_pwa_install_modal_shown_v1';

let deferredPrompt = null;
let iosGuideOpen = false;
let androidModalOpen = false;
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = window.navigator.standalone === true;
  return mq || iosStandalone;
}

export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (!isIosDevice()) return false;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Android/.test(ua);
  return isSafari || /Version\//.test(ua);
}

export function wasInstallDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function markInstallDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  deferredPrompt = null;
  androidModalOpen = false;
  notify();
}

export function wasInstallModalShown() {
  try {
    return localStorage.getItem(MODAL_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

export function markInstallModalShown() {
  try {
    localStorage.setItem(MODAL_SHOWN_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function getDeferredPrompt() {
  return deferredPrompt;
}

export function clearDeferredPrompt() {
  deferredPrompt = null;
  notify();
}

export function getIosGuideOpen() {
  return iosGuideOpen;
}

export function setIosGuideOpen(open) {
  iosGuideOpen = Boolean(open);
  notify();
}

export function getAndroidModalOpen() {
  return androidModalOpen;
}

export function setAndroidModalOpen(open) {
  androidModalOpen = Boolean(open);
  notify();
}

export async function promptPwaInstall() {
  const promptEvent = deferredPrompt;
  if (!promptEvent) return { outcome: 'unavailable' };
  try {
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    deferredPrompt = null;
    androidModalOpen = false;
    notify();
    if (choice?.outcome === 'accepted') {
      markInstallDismissed();
    }
    return choice || { outcome: 'dismissed' };
  } catch {
    return { outcome: 'error' };
  }
}

export function subscribePwaInstall(listener) {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
}

export function initPwaInstallCapture() {
  if (typeof window === 'undefined') return;
  if (window.__saPwaInstallCaptureInit) return;
  window.__saPwaInstallCaptureInit = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    markInstallDismissed();
    notify();
  });
}

// Auto-init no import (browser)
initPwaInstallCapture();
